import { useCallback, useEffect, useState } from "react";

import { getProjectAggregate } from "../../../../service/publicquery";
import { Status } from "../../../../service/Shared";
import { getCountryNameByCode } from "../../../../utils";
import { ChartData } from "../../../../widget";

interface UseKeyFactsSectionReturnData {
  totalActiveProjectsChartData: ChartData;
  unitsCreatedChartData: ChartData;
  totalPlannedCarbonSequestrationChartData: ChartData;
  isLoading: boolean;
}

export const UseKeyFactsSection = (organisationUuid: string | undefined): UseKeyFactsSectionReturnData => {
  const [totalActiveProjectsChartData, setTotalActiveProjectsChartData] = useState<ChartData>({});

  const [unitsCreatedChartData, setUnitsCreatedChartData] = useState<ChartData>({});

  const [isLoading, setIsLoading] = useState(true);

  const [totalPlannedCarbonSequestrationChartData, setTotalPlannedCarbonSequestrationChartData] = useState<ChartData>(
    {}
  );

  const fetchData = useCallback(async (): Promise<void> => {
    if (organisationUuid) {
      const [totalProjectsRes, unitsAggregateRes, projectLocationsRes] = await Promise.all([
        getProjectAggregate({
          filterBy: "standard",
          filterOperator: "eq",
          filterValue: organisationUuid,
          groupBy: "status",
          aggregation: "count",
          aggregate: "id",
        }),
        getProjectAggregate({
          filterBy: "standard",
          filterOperator: "eq",
          filterValue: organisationUuid,
          aggregation: "sum",
          aggregate: "cached_total_unit",
          groupBy: "unitMeasurement",
        }),
        getProjectAggregate({
          filterBy: "standard",
          filterOperator: "eq",
          filterValue: organisationUuid,
          groupBy: "addressCountryCode",
          aggregation: "count",
          aggregate: "id",
        }),
      ]);

      if (totalProjectsRes.status === Status.Success && totalProjectsRes.data) {
        setTotalActiveProjectsChartData(
          totalProjectsRes.data.concat(totalProjectsRes.data).reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[current.key.replace(" Carbon ", " ")] = current.value;

            return previous;
          }, {})
        );
      }

      if (unitsAggregateRes.status === Status.Success && unitsAggregateRes.data) {
        setUnitsCreatedChartData(Object.fromEntries(unitsAggregateRes.data.map((item) => [item.key, item.value])));
      }

      if (projectLocationsRes.status === Status.Success && projectLocationsRes.data) {
        setTotalPlannedCarbonSequestrationChartData(
          projectLocationsRes.data.reduce<ChartData>((previous, current) => {
            // eslint-disable-next-line no-param-reassign
            previous[getCountryNameByCode(current.key) || ""] = current.value;

            return previous;
          }, {})
        );
      }
    }
  }, []);

  useEffect(() => {
    fetchData().finally(() => setIsLoading(false));
  }, [fetchData]);

  return {
    totalActiveProjectsChartData,
    unitsCreatedChartData,
    totalPlannedCarbonSequestrationChartData,
    isLoading,
  };
};
